import { jsx as _jsx } from "react/jsx-runtime";
import format from 'date-fns/format';
import parseISO from 'date-fns/parseISO';
import { decode } from 'html-entities';
import { PropertyCard } from '@awaze/design-system.compositions.property-card';
import { getActiveDepositScheme } from '@awaze/mfe-common.configs.brand';
import { formatPropertyPageURL } from '@awaze/mfe-common.utils.pdp-url';
import { PropertyListSkeleton } from './property-list-skeleton';
import { StyledPropertyList } from './property-list.styles';
import { formatIncludedExtras, shouldAddImageAttribute, } from './property-list.utils';
/**
 * The property list takes in the results from OneSearch and other related configuration (e.g. top USP lists, deposit schemes, loading state, etc) to display the results as individual property cards.

 * This component contains the business logic required to apply certain functionality to the property cards such as:
 *
 * - Which USPs to display
 * - If a deposit scheme should be displayed
 * - How the property detail page URL is constructed
 * - How the property image URL is constructed
 *
 * The property card component, which this component depends on, is a more 'dumb' presentational component and will simply display what you pass into it.
*/
export const PropertyList = ({ config, deposits, results, loading, nights, onPropertyLinkClick = () => null, onCarouselInteractFactory = () => null, }) => {
    const { resultsPageSize, searchParameters = '', dateToday, accommodationType, translations, pdpUrlSegment, numberImagesToEagerLoad = 0, numberImagesFetchPriorityHigh = 0, numberImagesPriority = 0, } = config;
    const params = new URLSearchParams(searchParameters);
    dateToday.setHours(0, 0, 0, 0);
    return (_jsx(StyledPropertyList, { "aria-label": "properties", children: loading ? (_jsx(PropertyListSkeleton, { resultsPageSize: resultsPageSize })) : (results.map(({ property, priceFrom = 0, startDate, price, wasPrice, code, displayName = '', siteName, siteCode, grade, location, rhs3, averageReviewScore, features, USPs, lengthOfStay, hasCallout, hasPetsFree, cardinality, images, urgencyNudgeMessaging, }, resultIndex) => {
            const decodedTitle = decode(displayName);
            const gradeNumber = parseInt(grade) || null;
            const FUSION_DATE_FORMAT = 'dd-MM-yyyy';
            if (startDate) {
                params.set('start', format(parseISO(startDate), FUSION_DATE_FORMAT));
            }
            if (lengthOfStay) {
                params.set('nights', lengthOfStay.toString());
            }
            const formattedParams = params.toString().replace(/%21/g, ''); // HACK: Remove ! (ideally should be fixed in fusion)
            const pageURL = formatPropertyPageURL(pdpUrlSegment, displayName, code, formattedParams, siteName, siteCode);
            const priceForActiveDepositScheme = cardinality === 'SINGLE' ? price : priceFrom;
            const sleepsForActiveDepositScheme = cardinality === 'SINGLE' ? property.sleeps : 0;
            const activeDepositScheme = startDate &&
                getActiveDepositScheme(deposits, dateToday, new Date(startDate), sleepsForActiveDepositScheme, priceForActiveDepositScheme, accommodationType || 'cottages');
            const includedExtras = formatIncludedExtras(property.includedExtras);
            const eagerLoading = shouldAddImageAttribute(numberImagesToEagerLoad, resultIndex);
            const fetchPriority = shouldAddImageAttribute(numberImagesFetchPriorityHigh, resultIndex)
                ? 'high'
                : 'auto';
            const priority = shouldAddImageAttribute(numberImagesPriority, resultIndex);
            return (_jsx("li", { children: _jsx(PropertyCard, { activeDepositScheme: activeDepositScheme, priceFrom: priceFrom, price: price, wasPrice: wasPrice, nights: lengthOfStay ?? nights, startDate: startDate, code: `code-${code}`, title: decodedTitle, location: location, rhs3: rhs3, images: images, grade: gradeNumber, guests: property.sleeps, hasPetsFree: hasPetsFree, hasCallout: hasCallout, pets: property.pets, pageURL: pageURL, bathrooms: property.bathrooms, bedrooms: property.bedrooms, score: averageReviewScore, USPs: USPs, includedExtras: includedExtras, features: features, cardinality: cardinality, urgencyNudgeMessaging: urgencyNudgeMessaging, onPropertyLinkClick: () => onPropertyLinkClick({
                        propertyCode: code,
                    }), translations: {
                        ...translations,
                        propertyLinkAriaLabel: translations.propertyLinkAriaLabel(decodedTitle),
                    }, imageConfig: {
                        eagerLoading,
                        fetchPriority,
                        priority,
                    }, onCarouselInteractCallback: onCarouselInteractFactory(images, displayName) }) }, code));
        })) }));
};
export default PropertyList;
//# sourceMappingURL=property-list.js.map